import hotkeys from 'hotkeys-js';

/**
 * Shared hotkey utilities for consistent behavior across all hotkey handlers
 */

let hotkeysInitialized = false;

/**
 * Initialize hotkeys library settings. Should be called once before registering any hotkeys.
 * Safe to call multiple times - will only initialize once.
 */
export const initializeHotkeys = (): void => {
    if (!hotkeysInitialized) {
        // Allow hotkeys to fire in all elements so we can call preventDefault()
        // to stop special characters from being typed on macOS (Alt+key = special char)
        hotkeys.filter = () => true;
        hotkeysInitialized = true;
    }
};

/**
 * Check if any blocking modal dialog is open (e.g., Save modal, confirmation dialogs).
 * Hotkeys should not execute when a modal is open that cannot be closed.
 * A modal is blocking only if it has no close button (modal, slideout, or popover close button).
 */
export const isBlockingModalOpen = (): boolean => {
    const dialogs = document.querySelectorAll('[role="dialog"]');
    for (let i = 0; i < dialogs.length; i++) {
        const dialog = dialogs[i];
        const hasCloseButton =
            dialog.querySelector('[data-testid^="ids-modal-close-button"]') ||
            dialog.querySelector('[data-testid^="ids-slideout-close-button"]') ||
            dialog.querySelector('[data-testid^="ids-popover-close-button"]');
        if (!hasCloseButton) {
            return true;
        }
    }
    return false;
};

/**
 * Close any open non-blocking overlays (Snackbars, Modals with close button, SlideOuts, Popovers).
 * Used to dismiss notifications and panels when a hotkey action is triggered.
 */
export const closeOpenSnackbars = (): void => {
    const snackbarCloseButtons = document.querySelectorAll('[data-testid^="snackbar-close-button"], .Toastify__close-button');
    snackbarCloseButtons.forEach((button) => (button as HTMLElement).click());

    const modalCloseButtons = document.querySelectorAll('[data-testid^="ids-modal-close-button"]');
    modalCloseButtons.forEach((button) => (button as HTMLElement).click());

    const slideoutCloseButtons = document.querySelectorAll('[data-testid^="ids-slideout-close-button"]');
    slideoutCloseButtons.forEach((button) => (button as HTMLElement).click());

    const popoverCloseButtons = document.querySelectorAll('[data-testid^="ids-popover-close-button"]');
    popoverCloseButtons.forEach((button) => (button as HTMLElement).click());
};

/**
 * Generate hotkey string with CapsLock support.
 * @param key - The key to create hotkey for (e.g., 't' for Trade In)
 * @returns Hotkey string that works with and without CapsLock (e.g., 'alt+t,CapsLock+alt+t')
 */
export const createHotkeyWithCapsLock = (key: string): string => {
    return `alt+${key},CapsLock+alt+${key}`;
};

/**
 * Generate multiple hotkeys with CapsLock support.
 * @param keys - Array of keys to create hotkeys for
 * @returns Combined hotkey string
 */
export const createHotkeysWithCapsLock = (keys: string[]): string => {
    return keys.map((key) => createHotkeyWithCapsLock(key)).join(',');
};
